// Pro games for the simulated period and the stat events players generate
// inside them. Events are pre-generated deterministically; a player's stat
// line at time t is the sum of events with progress <= t. That gives live
// deltas and a play feed for free.

import type { StatLine } from '@fsp/sports-core';
import { hashSeed, rng } from './rng';
import type { SimPosition } from './players';
import { formatInZone } from '@fsp/domain';
import { football } from '@fsp/sports-football';

const ABBREVIATIONS = new Map(football.proTeams.map((t) => [t.id, t.abbreviation]));

/** A club's code as a person reads it: the Rams' `LA` reads `LAR` (`ProTeam.abbreviation`). */
export function clubLabel(code: string): string {
  return ABBREVIATIONS.get(code) ?? code;
}

export const GAME_SECONDS = 3 * 3600 + 15 * 60;

export interface GameEvent {
  /** Progress through the game, 0..1. */
  p: number;
  line: StatLine;
  description: string;
}

export interface ProGame {
  id: string;
  home: string;
  away: string;
  /** ms since epoch */
  startAt: number;
  /** Final score, used for DST and the score label. */
  homeFinal: number;
  awayFinal: number;
}

/** Kickoff slots for a simulated Week: Thursday night, three Sunday windows, Sunday night, Monday night. All UTC. */
export function weekSlots(sundayUtcMidnight: number): { label: string; at: number }[] {
  const H = 3600 * 1000;
  const sun = sundayUtcMidnight;
  return [
    // Thu 8:15 PM ET = Fri 00:15Z. Until 2026-10-25 this was three days back,
    // Thu 00:15Z, a Wednesday night in the East; the bot league's roll call
    // of game days found it.
    { label: 'Thu 8:15', at: sun - 2 * 24 * H + 0.25 * H },
    { label: 'Sun 1:00', at: sun + 17 * H },
    { label: 'Sun 4:05', at: sun + 20 * H + 5 * 60 * 1000 },
    { label: 'Sun 4:25', at: sun + 20 * H + 25 * 60 * 1000 },
    { label: 'Sun 8:20', at: sun + 24 * H + 0.33 * H },
    { label: 'Mon 8:15', at: sun + 48 * H + 0.25 * H },
  ];
}

export function buildProSchedule(
  teams: readonly string[],
  sundayUtcMidnight: number,
  seed: number,
): {
  games: ProGame[];
  byes: string[];
} {
  const r = rng(hashSeed(seed, 'schedule'));
  const order = r.shuffle(teams);
  const byes = order.slice(0, 4).sort();
  const playing = order.slice(4);
  const slots = weekSlots(sundayUtcMidnight);
  const games: ProGame[] = [];
  // 28 teams → 14 games: 1 Thu, 8 at 1:00, 2 at 4:05, 2 at 4:25, 1 SNF; Monday gets none in this sim (byes cover it).
  const plan = [0, 1, 1, 1, 1, 1, 1, 1, 1, 2, 2, 3, 3, 4];
  for (let i = 0; i < 14; i++) {
    const home = playing[i * 2]!;
    const away = playing[i * 2 + 1]!;
    const slot = slots[plan[i]!]!;
    const homeFinal = r.int(13, 34);
    const awayFinal = r.int(10, 31);
    games.push({ id: `g-${home}-${away}`, home, away, startAt: slot.at, homeFinal, awayFinal });
  }
  return { games, byes };
}

export function gameProgress(game: ProGame, nowMs: number): number {
  return Math.max(0, Math.min(1, (nowMs - game.startAt) / (GAME_SECONDS * 1000)));
}

export function gameLabel(
  game: ProGame,
  nowMs: number,
  /** The zone a kickoff is written in: the reader's (B-181). */
  zone: string,
): { state: 'pre' | 'live' | 'final'; label: string; score: string | null } {
  const p = gameProgress(game, nowMs);
  if (nowMs < game.startAt) {
    return { state: 'pre', label: kickoffLabel(game.startAt, zone), score: null };
  }
  const [home, away] = [clubLabel(game.home), clubLabel(game.away)];
  const score = `${home} ${Math.round(game.homeFinal * Math.min(1, p * 1.05))}–${away} ${Math.round(game.awayFinal * Math.min(1, p * 1.05))}`;
  if (p >= 1)
    return {
      state: 'final',
      label: 'Final',
      score: `${home} ${game.homeFinal}–${away} ${game.awayFinal}`,
    };
  const q = Math.min(4, Math.floor(p * 4) + 1);
  const remaining = 15 * (1 - (p * 4 - (q - 1)));
  const mm = Math.floor(remaining);
  const ss = Math.floor((remaining - mm) * 60);
  return { state: 'live', label: `Q${q} ${mm}:${String(ss).padStart(2, '0')}`, score };
}

/** "Sun 11:00 AM MDT": the practice world's kickoff, in the reader's zone and named (B-181). */
export function kickoffLabel(startAt: number, zone: string): string {
  return formatInZone(startAt, zone, { weekday: 'short', hour: 'numeric', minute: '2-digit' });
}

function yardsFor(r: ReturnType<typeof rng>, ppg: number, base: number, spread: number): number {
  return Math.max(-3, Math.round(r.around(base * (0.6 + ppg / 20), spread)));
}

/** Deterministic events for one player in one game. `talent` is the ppg baseline. */
export function playerEvents(
  playerId: string,
  gameId: string,
  pos: SimPosition,
  ppg: number,
  seed: number,
): GameEvent[] {
  const r = rng(hashSeed(seed, gameId, playerId));
  const events: GameEvent[] = [];
  const push = (p: number, line: StatLine, description: string) =>
    events.push({ p, line, description });
  const boom = r.around(1, 0.3, 0.4, 1.8);
  const count = (n: number) => Math.max(1, Math.round(n * boom));

  switch (pos) {
    case 'QB': {
      const n = count(r.int(9, 14));
      for (let i = 0; i < n; i++) {
        const p = r.next();
        if (r.chance(0.16)) {
          push(p, { pass_att: 1 }, 'incomplete pass');
          continue;
        }
        const yd = yardsFor(r, ppg, 13, 9);
        if (r.chance(0.11 * boom))
          push(p, { pass_att: 1, pass_cmp: 1, pass_yd: yd, pass_td: 1 }, `${yd}-yd touchdown pass`);
        else push(p, { pass_att: 1, pass_cmp: 1, pass_yd: yd }, `${yd}-yd completion`);
      }
      if (r.chance(0.35)) push(r.next(), { pass_att: 1, pass_int: 1 }, 'interception thrown');
      if (r.chance(0.5)) {
        const yd = r.int(3, 18);
        push(r.next(), { rush_att: 1, rush_yd: yd }, `${yd}-yd scramble`);
      }
      if (r.chance(0.2))
        push(r.next(), { rush_att: 1, rush_yd: 2, rush_td: 1 }, '2-yd rushing touchdown');
      if (r.chance(0.3)) push(r.next(), { pass_sack: 1 }, 'sacked');
      break;
    }
    case 'RB': {
      const n = count(r.int(8, 13));
      for (let i = 0; i < n; i++) {
        const p = r.next();
        const yd = yardsFor(r, ppg, 5, 6);
        if (r.chance(0.07 * boom))
          push(
            p,
            { rush_att: 1, rush_yd: Math.max(1, yd), rush_td: 1 },
            `${Math.max(1, yd)}-yd rushing touchdown`,
          );
        else push(p, { rush_att: 1, rush_yd: yd }, `${yd}-yd run`);
      }
      const targets = count(r.int(1, 5));
      for (let i = 0; i < targets; i++) {
        const p = r.next();
        if (r.chance(0.25)) push(p, { rec_tgt: 1 }, 'target, incomplete');
        else {
          const yd = yardsFor(r, ppg, 7, 6);
          push(p, { rec_tgt: 1, rec: 1, rec_yd: yd }, `${yd}-yd reception`);
        }
      }
      if (r.chance(0.08)) push(r.next(), { fum: 1, fum_lost: 1 }, 'fumble lost');
      break;
    }
    case 'WR':
    case 'TE': {
      const n = count(r.int(pos === 'WR' ? 5 : 3, pos === 'WR' ? 10 : 7));
      for (let i = 0; i < n; i++) {
        const p = r.next();
        if (r.chance(0.33)) {
          push(p, { rec_tgt: 1 }, 'target, incomplete');
          continue;
        }
        const yd = yardsFor(r, ppg, 12, 9);
        if (r.chance(0.09 * boom))
          push(
            p,
            { rec_tgt: 1, rec: 1, rec_yd: Math.max(1, yd), rec_td: 1 },
            `${Math.max(1, yd)}-yd touchdown catch`,
          );
        else push(p, { rec_tgt: 1, rec: 1, rec_yd: yd }, `${yd}-yd catch`);
      }
      if (pos === 'WR' && r.chance(0.15)) {
        const yd = r.int(4, 22);
        push(r.next(), { rush_att: 1, rush_yd: yd }, `${yd}-yd end-around`);
      }
      break;
    }
    case 'K': {
      const fgs = count(r.int(1, 4));
      for (let i = 0; i < fgs; i++) {
        const dist = r.int(22, 56);
        const key =
          dist < 20
            ? 'fg_0_19'
            : dist < 30
              ? 'fg_20_29'
              : dist < 40
                ? 'fg_30_39'
                : dist < 50
                  ? 'fg_40_49'
                  : 'fg_50p';
        if (r.chance(0.12)) push(r.next(), { fg_miss: 1 }, `missed ${dist}-yd field goal`);
        else push(r.next(), { [key]: 1 }, `${dist}-yd field goal`);
      }
      const xps = r.int(1, 4);
      for (let i = 0; i < xps; i++) push(r.next(), { xp_made: 1 }, 'extra point');
      break;
    }
    case 'DST': {
      const drives = r.int(3, 6);
      for (let i = 0; i < drives; i++) {
        const p = r.next();
        if (r.chance(0.55)) push(p, { dst_pts_allowed: 7 }, 'allowed a touchdown');
        else push(p, { dst_pts_allowed: 3 }, 'allowed a field goal');
      }
      const sacks = count(r.int(1, 4));
      for (let i = 0; i < sacks; i++) push(r.next(), { dst_sack: 1 }, 'sack');
      if (r.chance(0.45)) push(r.next(), { dst_int: 1 }, 'interception');
      if (r.chance(0.3))
        push(r.next(), { dst_fum_rec: 1, dst_ff: 1 }, 'forced and recovered a fumble');
      if (r.chance(0.08)) push(r.next(), { dst_td: 1 }, 'defensive touchdown');
      break;
    }
  }
  return events.sort((a, b) => a.p - b.p);
}

export function lineAt(events: readonly GameEvent[], p: number): StatLine {
  const out: Record<string, number> = {};
  for (const e of events) {
    if (e.p > p) break;
    for (const [k, v] of Object.entries(e.line)) out[k] = (out[k] ?? 0) + v;
  }
  return out;
}
